import type { CSSProperties, FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { gsap, useGSAP } from '@/lib/gsap'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { ButtonLink } from '@/components/generals/button'
import { AnimatedNumber } from '@/components/generals/animated-number'

const STATEMENT = 'Veyra helps finance managers eliminate manual work, gain real-time visibility into financial performance, and make faster, data-driven decisions through intelligent AI automation.'

const HIGHLIGHTS = [
    'eliminate manual work,',
    'real-time visibility',
    'data-driven decisions',
    'intelligent AI automation.'
]

const STATS = [
    { value: 500, prefix: '', suffix: '+', label: 'Companies optimizing their finances' },
    { value: 80, prefix: '', suffix: '%', label: 'Faster financial reporting' },
    { value: 20, prefix: '$', suffix: 'B+', label: 'Financial data processed' }
]

const markHighlights = (words: Array<string>): Set<number> => {
    const marked = new Set<number>()

    for (const phrase of HIGHLIGHTS) {
        const parts = phrase.split(' ')

        for (let start = 0; start <= words.length - parts.length; start++) {
            if (parts.every((part, offset) => words[start + offset] === part)) {
                parts.forEach((_, offset) => marked.add(start + offset))
            }
        }
    }

    return marked
}

interface AboutStatementProps {
    eyebrow?: string
    showLink?: boolean
}

export const AboutStatement: FC<AboutStatementProps> = ({ eyebrow = 'About us', showLink = true }): ReactNode => {
    const textRef = useRef<HTMLHeadingElement>(null)
    const words = STATEMENT.split(' ')
    const marked = markHighlights(words)

    useGSAP(() => {
        const element = textRef.current

        if (!element) return

        const media = gsap.matchMedia()

        media.add('(prefers-reduced-motion: no-preference)', () => {
            gsap.fromTo(
                element.querySelectorAll('[data-word]'),
                { opacity: 0.16 },
                {
                    opacity: 1,
                    ease: 'none',
                    stagger: 0.08,
                    scrollTrigger: {
                        trigger: element,
                        start: 'top 82%',
                        end: 'bottom 45%',
                        scrub: 0.6
                    }
                }
            )
        })

        return () => media.revert()
    }, { scope: textRef })

    return (
        <section id='about' aria-labelledby='about-title' className='relative overflow-hidden bg-burgundy text-cream'>
            <div aria-hidden='true' className='pointer-events-none absolute -left-40 top-24 size-[520px] rounded-full bg-burgundy-soft/60 blur-3xl' />

            <Container className='relative py-28 md:py-40'>
                <div className='flex items-center justify-between gap-6 border-b border-cream/10 pb-6'>
                    <Eyebrow tone='light'>{eyebrow}</Eyebrow>

                    <span className='font-mono text-[11px] uppercase tracking-[0.2em] text-cream/40'>
                        Financial clarity, automated
                    </span>
                </div>

                <h2
                    ref={textRef}
                    id='about-title'
                    className='mt-14 max-w-[1180px] font-heading text-[clamp(1.85rem,4.1vw,4.25rem)] font-medium leading-[1.08] tracking-[-0.035em] md:mt-20'
                >
                    {words.map((word, index) => (
                        <span key={`${word}-${index}`}>
                            <span data-word='' className={cn(marked.has(index) ? 'text-beige' : 'text-cream')}>
                                {word}
                            </span>
                            {index < words.length - 1 && ' '}
                        </span>
                    ))}
                </h2>

                <dl className='mt-20 grid gap-y-10 sm:grid-cols-3 md:mt-28'>
                    {STATS.map((stat, index) => (
                        <div
                            key={stat.label}
                            data-reveal=''
                            className={cn(
                                'flex flex-col border-t border-cream/15 pt-6 sm:pr-8',
                                index > 0 && 'sm:border-l sm:pl-8'
                            )}
                            style={{ '--reveal-delay': `${index * 120}ms` } as CSSProperties}
                        >
                            <dt className='order-2 mt-3 text-sm text-cream/60'>{stat.label}</dt>

                            <dd className='order-1 font-heading text-display-lg font-medium text-cream'>
                                <AnimatedNumber value={stat.value} prefix={stat.prefix} suffix={stat.suffix} duration={1800} />
                            </dd>
                        </div>
                    ))}
                </dl>

                {showLink && (
                    <div className='mt-16 flex justify-start'>
                        <ButtonLink href='/about' variant='outline-light' arrow>
                            Our story
                        </ButtonLink>
                    </div>
                )}
            </Container>
        </section>
    )
}
